import _ from 'lodash';
import { RequestStatus } from '../../enums';
import { makeAsyncReducerHandlers } from '../../helpers';
import {
  UsersActionTypes,
  ProjectsActionTypes,
  QuestionnairesActionTypes,
  InterviewsActionTypes,
  BooksActionTypes,
} from '../../actions/types';
import { UserRole } from '../../../../../shared/enums';

const fetchMoreUsers = makeAsyncReducerHandlers(
  UsersActionTypes.fetchMoreUsers,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: [...state.items, ...action.payload.users],
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const searchUsers = makeAsyncReducerHandlers(
  UsersActionTypes.searchUsers,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const items = Array.from(state.items);
    action.payload.users.forEach((user) => {
      const index = items.findIndex(item => item._id === user._id);
      if (index === -1) {
        items.push(user);
      } else {
        items.splice(index, 1, user);
      }
    });

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchDataToCreateProject = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchDataToCreateProject,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchDataToEditProject = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchDataToEditProject,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const editProjectWithRelatedData = makeAsyncReducerHandlers(
  ProjectsActionTypes.editProjectWithRelatedData,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { customer } = action.payload;
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === customer._id);

    if (index === -1) {
      items.push(customer);
    } else {
      items.splice(index, 1, customer);
    }

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const deleteUser = makeAsyncReducerHandlers(
  UsersActionTypes.deleteUser,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { user } = action.payload;
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === user._id);

    if (index === -1) {
      items.push(user);
    } else {
      items.splice(index, 1, user);
    }
    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const sendReinvite = makeAsyncReducerHandlers(
  UsersActionTypes.sendReinvite,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { user } = action.payload;
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === user._id);

    if (index === -1) {
      items.push({ ...user });
    } else {
      items.splice(index, 1, { ...items[index], ...user });
    }

    return ({
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    });
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getUserById = makeAsyncReducerHandlers(
  UsersActionTypes.getUserById,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: [action.payload.user],
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateUser = makeAsyncReducerHandlers(
  UsersActionTypes.updateUser,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { user } = action.payload;
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === user._id);

    if (index === -1) {
      items.push(user);
    } else {
      items.splice(index, 1, user);
    }
    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addUser = makeAsyncReducerHandlers(
  UsersActionTypes.addUser,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getUsers = makeAsyncReducerHandlers(
  UsersActionTypes.getUsers,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getUsersForExcel = makeAsyncReducerHandlers(
  UsersActionTypes.getUsersForExcel,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchProjectOverviewDataByProjectId = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchProjectOverviewDataByProjectId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.members,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchProjectOverviewDataByCustomerId = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchProjectOverviewDataByCustomerId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.members,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getWritersMatch = makeAsyncReducerHandlers(
  UsersActionTypes.getWritersMatch,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.writers,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getExcelWritersMatch = makeAsyncReducerHandlers(
  UsersActionTypes.getExcelWritersMatch,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getOfflineWritersMatch = makeAsyncReducerHandlers(
  UsersActionTypes.getOfflineWritersMatch,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.writers,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getExcelOfflineWritersMatch = makeAsyncReducerHandlers(
  UsersActionTypes.getExcelOfflineWritersMatch,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addWriterToShortlist = makeAsyncReducerHandlers(
  UsersActionTypes.addWriterToShortlist,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { writer } = action.payload;
    const items = Array.from(state.items);
    const foundIndex = items.findIndex(item => item._id === writer._id);

    if (foundIndex !== -1) {
      const maxSequenceIndex = items
        .filter(item => item.isInShortlist)
        .reduce((a, b) => (a.sequenceIndex > b.sequenceIndex ? a : b), {}).sequenceIndex;
      items[foundIndex].sequenceIndex = maxSequenceIndex + 1;
      items[foundIndex].isInShortlist = writer.isInShortlist;
    }

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const removeWriterFromShortlist = makeAsyncReducerHandlers(
  UsersActionTypes.removeWriterFromShortlist,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const writersList = {
      items: null,
      getItems() {
        return this.items;
      },
      initItems(items) {
        this.items = Array.from(items);
      },
      findIndex(writer) {
        return this.items.findIndex(item => item._id === writer._id);
      },
      updateWriter(writer, index) {
        this.items[index].isInShortlist = writer.isInShortlist;
        delete this.items[index].sequenceIndex;
      },
      sequenceShortlist() {
        return this.items
          .filter(item => item.isInShortlist)
          .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
          .map((item, index) => ({ ...item, sequenceIndex: index }));
      },
      mergeList(list) {
        list.forEach((a) => {
          const index = this.items.findIndex(b => b._id === a._id);
          if (index !== -1) {
            this.items[index] = a;
          }
        });
      },
      updateItems(writer) {
        const index = this.findIndex(writer);
        if (index !== -1) {
          this.updateWriter(writer, index);
          const shortlist = this.sequenceShortlist();
          this.mergeList(shortlist);
        }
      },
    };

    writersList.initItems(state.items);
    writersList.updateItems(action.payload.writer);

    return {
      items: writersList.getItems(),
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getUserWithMiscData = makeAsyncReducerHandlers(
  UsersActionTypes.getUserWithMiscData,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.shouldLoadMoreProjects ? state.items : [action.payload.user],
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchQuestionnaireByCustomerId = makeAsyncReducerHandlers(
  QuestionnairesActionTypes.fetchQuestionnaireByCustomerId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getInterviewsInfo = makeAsyncReducerHandlers(
  InterviewsActionTypes.getInterviewsInfo,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const toggleArchivingUser = makeAsyncReducerHandlers(
  UsersActionTypes.toggleArchivingUser,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { user } = action.payload;
    const items = [...state.items];
    const index = items.findIndex(item => item._id === user._id);

    if (index === -1) {
      items.push({ ...items[index], isArchived: user.isArchived });
    } else {
      items.splice(index, 1, { ...items[index], isArchived: user.isArchived });
    }

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const removeAvatar = makeAsyncReducerHandlers(
  UsersActionTypes.removeAvatar,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === action.payload.user._id);

    if (index !== -1) items[index] = action.payload.user;

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const removeUnsavedUserAvatar = makeAsyncReducerHandlers(
  UsersActionTypes.removeUnsavedUserAvatar,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const fetchTimelineDataByCustomer = makeAsyncReducerHandlers(
  ProjectsActionTypes.fetchTimelineDataByCustomer,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.members,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getBookAndResourcesByProjectId = makeAsyncReducerHandlers(
  BooksActionTypes.getBookAndResourcesByProjectId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getBookAndResourcesByCustomerId = makeAsyncReducerHandlers(
  BooksActionTypes.getBookAndResourcesByCustomerId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.users,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addAvatarPhoto = makeAsyncReducerHandlers(
  UsersActionTypes.addAvatarPhoto,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === action.payload.user._id);

    if (index !== -1) items[index] = action.payload.user;

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getDataFromHubspot = makeAsyncReducerHandlers(
  ProjectsActionTypes.getDataFromHubspot,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const hubspotUsers = [];
    action.payload.users.forEach((user) => {
      const userToEditIndex = state.items.findIndex(({ _id }) => _id === user._id);
      if (userToEditIndex >= 0) {
        state.items[userToEditIndex] = _.merge(state.items[userToEditIndex], user);
      } else {
        hubspotUsers.push(user);
      }
    });

    return ({
      items: [...state.items, ...hubspotUsers],
      status: RequestStatus.SUCCEEDED,
      error: null,
    });
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getUserByToken = makeAsyncReducerHandlers(
  UsersActionTypes.getUserByToken,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: [action.payload.user],
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateHubspotProjectsCount = makeAsyncReducerHandlers(
  UsersActionTypes.updateHubspotProjectsCount,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const items = Array.from(state.items);
    const index = items.findIndex(item => item._id === action.payload.user._id);

    if (index !== -1) items[index] = { ...items[index], ...action.payload.user };

    return {
      items,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateWritersProjectCount = makeAsyncReducerHandlers(
  UsersActionTypes.updateWritersProjectCount,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => {
    const { writers } = action.payload;

    state.items.forEach((user) => {
      if (user.role === UserRole.WRITER) {
        const index = writers.findIndex(item => item._id === user._id);
        user.activeProjectsCount = writers[index].hubspotProjects.count;
      }
    });

    return ({
      items: [...state.items],
      status: RequestStatus.SUCCEEDED,
      error: null,
    });
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateWritersDBSScreened = makeAsyncReducerHandlers(
  UsersActionTypes.updateWritersDBSScreened,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

export default {
  fetchProjectOverviewDataByProjectId,
  fetchProjectOverviewDataByCustomerId,
  fetchMoreUsers,
  searchUsers,
  fetchDataToCreateProject,
  fetchDataToEditProject,
  editProjectWithRelatedData,
  deleteUser,
  sendReinvite,
  getUserById,
  getUsers,
  getUsersForExcel,
  updateUser,
  addUser,
  getWritersMatch,
  getOfflineWritersMatch,
  addWriterToShortlist,
  removeWriterFromShortlist,
  getUserWithMiscData,
  fetchQuestionnaireByCustomerId,
  getInterviewsInfo,
  toggleArchivingUser,
  removeAvatar,
  fetchTimelineDataByCustomer,
  getBookAndResourcesByProjectId,
  getBookAndResourcesByCustomerId,
  addAvatarPhoto,
  getExcelWritersMatch,
  getExcelOfflineWritersMatch,
  removeUnsavedUserAvatar,
  getDataFromHubspot,
  getUserByToken,
  updateHubspotProjectsCount,
  updateWritersProjectCount,
  updateWritersDBSScreened,
};
